import React from 'react';
import PropTypes from 'prop-types';
import groupBy from 'lodash/groupBy';
import { Title } from 'smartsuite-ui/lib/typography/Title';
import { InfoAlert } from 'smartsuite-ui/lib/alerts/InfoAlert';
import DashboardGroupedWidgets from './DashboardGroupedWidgets';
import { useLabelContext } from '../shared/hooks/labelContext';

function widgetTitleInSearchText(widgetTitle, lowerSearchText) {
    if (!widgetTitle) {
        return false;
    }

    return widgetTitle.toLowerCase().includes(lowerSearchText);
}

function viewTitleInSearchText(viewTitle, lowerSearchText) {
    if (!viewTitle) {
        return false;
    }

    return viewTitle.toLowerCase().includes(lowerSearchText);
}

function dashboardTitleInSearchText(dashboardTitle, lowerSearchText) {
    if (!dashboardTitle) {
        return false;
    }

    return dashboardTitle.toLowerCase().includes(lowerSearchText);
}

function getFilteredWidgets(widgets, searchText) {
    const lowerSearchText = searchText.toLowerCase();

    return widgets.filter(
        (widget) =>
            widgetTitleInSearchText(widget.stateData.title, lowerSearchText) ||
            viewTitleInSearchText(widget.viewTitle, lowerSearchText) ||
            dashboardTitleInSearchText(widget.dashboardTitle, lowerSearchText)
    );
}

export default function SearchResults(props) {
    const { dashboardLabels } = useLabelContext();

    const className = props.showSearch
        ? 'widget-library__search-view'
        : 'widget-library__search-view widget-library__search-view--closed';

    const matchedWidgets = getFilteredWidgets(
        props.availableWidgets,
        props.searchText
    );

    if (!matchedWidgets?.length) {
        return (
            <div className={className}>
                <Title cssClassModifiers={['size-small']}>
                    {dashboardLabels.searchResults}
                </Title>
                <InfoAlert>{dashboardLabels.noResultsFound}</InfoAlert>
            </div>
        );
    }

    const groupedWidgets = groupBy(
        matchedWidgets,
        (widget) => `${widget.dashboardKey}-${widget.viewKey}`
    );

    const orderedGroups = Object.keys(groupedWidgets).sort();

    return (
        <div className={className}>
            <Title cssClassModifiers={['size-small']}>
                {dashboardLabels.searchResults}
            </Title>
            {orderedGroups.map((groupKey) => (
                <DashboardGroupedWidgets
                    key={groupKey}
                    groupWidgets={groupedWidgets[groupKey]}
                    gridLayout={props.gridLayout}
                    incorrectlyConfiguredDashboards={
                        props.incorrectlyConfiguredDashboards
                    }
                    onAdd={props.onAdd}
                    setDraggingWidget={props.setDraggingWidget}
                />
            ))}
        </div>
    );
}

SearchResults.propTypes = {
    availableWidgets: PropTypes.instanceOf(Array).isRequired,
    gridLayout: PropTypes.instanceOf(Array).isRequired,
    incorrectlyConfiguredDashboards: PropTypes.instanceOf(Object).isRequired,
    onAdd: PropTypes.func.isRequired,
    searchText: PropTypes.string.isRequired,
    setDraggingWidget: PropTypes.func.isRequired,
    showSearch: PropTypes.bool.isRequired,
};
